import React, { ReactNode, useContext, useEffect, useState } from 'react';

import ReportContext from '@totem/components/controlSystemReport/ControlSystemReportContext';
import {
  ControlSystemReport,
  FilterOptions,
} from '@totem/components/controlSystemReport/dataTypes';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { CONTROL_SYSTEM_REPORT } from '@totem/utilities/endpoints';

export const containerConfiguration: FilterField[] = [
  {
    key: 'regionId',
    label: 'Region',
    sortField: '',
    filterField: 'region.id',
    multiple: true,
    values: [],
  },
  {
    key: 'buildingId',
    label: 'Building',
    sortField: '',
    filterField: 'building.id',
    multiple: true,
    values: [],
  },
  {
    key: 'controlSystemId',
    label: 'Control System',
    sortField: '',
    filterField: 'id',
    multiple: true,
    values: [],
  },
  {
    key: 'name',
    label: 'System Name',
    sortField: 'name',
    filterField: 'name',
    multiple: true,
    values: [],
  },
  {
    key: 'type',
    label: 'Type',
    sortField: '',
    filterField: 'systemType',
    multiple: true,
    values: [],
  },
  {
    key: 'vendor',
    label: 'Vendor',
    sortField: '',
    filterField: 'manufacturer',
    multiple: true,
    values: [],
  },
  {
    key: 'operatingSystem',
    label: 'Operating System',
    sortField: '',
    filterField: 'operatingSystem',
    multiple: true,
    values: [],
  },
  {
    key: 'searchName',
    label: 'Search',
    sortField: '',
    filterField: 'name',
    multiple: false,
    value: '',
    operator: 'cscontains',
    allowPersistence: false,
  },
];

type Props = {
  children?: ReactNode;
  onRecordTotalChanged?: (total: number) => void;
};

const ControlSystemReportContainer = ({
  children,
  onRecordTotalChanged,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    setFilterOptions: setContextFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
  } = useContext(InputContext);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [reportData, setReportData] = useState<ControlSystemReport>(null);
  const [isFilterOptionsLoaded, setIsFilterOptionsLoaded] =
    useState<boolean>(false);

  useEffect(() => {
    setIsFilterOptionsLoaded(false);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (!isFilterOptionsLoaded) {
      fetch(`${CONTROL_SYSTEM_REPORT}/filterOptions`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: FilterOptions) => {
          if (isNotNull(result)) {
            if (isNotNull(result.regions)) {
              setContextFilterOptions(
                'regionId',
                result.regions.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (isNotNull(result.buildings)) {
              setContextFilterOptions(
                'buildingId',
                result.buildings.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
            if (isNotNull(result.controlSystemNames)) {
              setContextFilterOptions(
                'name',
                result.controlSystemNames.map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
          }
        })
        .then(() => {
          setIsFilterOptionsLoaded(true);
        });
    }
  }, [isFilterOptionsLoaded, getApiToken]);

  useEffect(() => {
    setRefreshData(true);
  }, [
    userProfile.organization.id,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
  ]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();
      fetch(CONTROL_SYSTEM_REPORT, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: ControlSystemReport) => {
          setReportData(result);

          setTotalRecords(result.paging.totalRecords);
          if (isNotNull(onRecordTotalChanged)) {
            onRecordTotalChanged(result.paging.totalRecords);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  const handleAction = (newAction: string) => {
    switch (newAction) {
      case 'refresh':
        setRefreshData(true);
        break;
      default:
        break;
    }
  };

  return (
    <ReportContext.Provider
      value={{
        loading: isLoading,
        reportData,
        totalRecords,
        onAction: handleAction,
      }}
    >
      <div>{children}</div>
    </ReportContext.Provider>
  );
};
export default ControlSystemReportContainer;
